import { Box, Typography, Paper, styled } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const StyledTitle = styled(Typography)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledTitleRight = styled(StyledTitle)`
  flex: 1;
  text-align: center;
`;

const StyledTitleLeft = styled(StyledTitle)`
  flex: 2;
  border-right: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledInfo = styled(StyledTitle)`
  color: #232d3d;
`;

const StyledInfoLeft = styled(StyledInfo)`
  border-right: 2px solid rgba(226, 230, 243, 0.8);
  flex: 2;
`;

const StyledInfoRight = styled(StyledInfo)`
  flex: 1;
  text-align: center;
`;

const StatusCell = ({ status }) => {
  const isPass = status.toLowerCase() === "pass";
  return (
    <Box
      sx={{
        bgcolor: isPass ? "#e8f5e9" : "#ffebee",
        color: isPass ? "#2e7d32" : "#d32f2f",
        p: 1,
        textAlign: "center",
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase",
        flex: 1,
        border: "1px solid #f0f0f0",
        borderRadius: 1,
        mx: 0.5,
      }}
    >
      {status}
    </Box>
  );
};

const transformData = (patterns) => {
  const STATUS_COLORS = {
    "pass-pass-pass": "#10B981",
    "pass-pass-fail": "#6366F1",
    "pass-fail-pass": "#8B5CF6",
    "pass-fail-fail": "#F59E0B",
    "fail-pass-pass": "#60A5FA",
    "fail-pass-fail": "#F97316",
    "fail-fail-pass": "#EC4899",
    "fail-fail-fail": "#EF4444",
  };

  const COLORS = [
    "#00C49F",
    "#0088FE",
    "#FFBB28",
    "#FF8042",
    "#a855f7",
    "#FF6B6B",
    "#52b788",
    "#845EC2",
  ];

  const customOrder = [
    "pass-pass-pass",
    "fail-pass-pass",
    "fail-pass-fail",
    "fail-fail-pass",
    "pass-fail-fail",
    "pass-fail-pass",
    "pass-pass-fail",
    "fail-fail-fail",
  ];

  const dataArray = Object.entries(patterns).map(([name, value]) => ({
    name,
    value,
    statuses: name.split("-"),
    color: STATUS_COLORS[name] || COLORS[Math.floor(Math.random() * COLORS.length)],
  }));

  return dataArray.sort((a, b) => {
    const indexA = customOrder.indexOf(a.name);
    const indexB = customOrder.indexOf(b.name);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

// 1. PATTERN DETAILS TABLE COMPONENT
export function PatternDetailsTable({ patterns }) {
  const data = transformData(patterns);
  const total = Object.values(patterns).reduce((sum, value) => sum + value, 0);

  return (
    <Box
      flex={1}
      overflow="hidden"
      sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
    >
      <Box display="flex" bgcolor={"#2550D1"}>
        <StyledTitleLeft>
          Pattern
          <Box sx={{ display: "flex", mt: 1 }}>
            <Box sx={{ flex: 1, textAlign: "center", fontSize: "12px" }}>Latest Run</Box>
            <Box sx={{ flex: 1, textAlign: "center", fontSize: "12px" }}>Previous Run</Box>
            <Box sx={{ flex: 1, textAlign: "center", fontSize: "12px" }}>Two Runs Ago</Box>
          </Box>
        </StyledTitleLeft>
        <StyledTitleRight>Count</StyledTitleRight>
      </Box>
      {data.length > 0 ? (
        data.map((item, index) => (
          <Box key={index} display="flex" bgcolor={"#ffffff"}>
            <StyledInfoLeft>
              <Box sx={{ display: "flex", mt: 1 }}>
                <StatusCell status={item.statuses[0]} />
                <StatusCell status={item.statuses[1]} />
                <StatusCell status={item.statuses[2]} />
              </Box>
            </StyledInfoLeft>
            <StyledInfoRight>
              {item.value} {total > 0 ? `(${((item.value / total) * 100).toFixed(1)}%)` : "(0%)"}
            </StyledInfoRight>
          </Box>
        ))
      ) : (
        <Box display="flex" bgcolor={"#ffffff"}>
          <StyledInfoLeft>No patterns available</StyledInfoLeft>
          <StyledInfoRight>0 (0%)</StyledInfoRight>
        </Box>
      )}
    </Box>
  );
}

// 2. BAR GRAPH COMPONENT
export function PatternBarGraph({ patterns }) {
  const data = transformData(patterns);
  const total = Object.values(patterns).reduce((sum, value) => sum + value, 0);

  // Custom tooltip to show count and percentage
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : 0;
      const item = payload[0].payload;

      return (
        <Paper sx={{ p: 2, boxShadow: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {item.name}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <Typography variant="caption">Latest Run</Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  width: "100%",
                  textAlign: "center",
                  bgcolor: item.statuses[0] === "pass" ? "#e8f5e9" : "#ffebee",
                  color: item.statuses[0] === "pass" ? "#2e7d32" : "#d32f2f",
                }}
              >
                {item.statuses[0].toUpperCase()}
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <Typography variant="caption">Previous Run</Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  width: "100%",
                  textAlign: "center",
                  bgcolor: item.statuses[1] === "pass" ? "#e8f5e9" : "#ffebee",
                  color: item.statuses[1] === "pass" ? "#2e7d32" : "#d32f2f",
                }}
              >
                {item.statuses[1].toUpperCase()}
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              <Typography variant="caption">Two Runs Ago</Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  width: "100%",
                  textAlign: "center",
                  bgcolor: item.statuses[2] === "pass" ? "#e8f5e9" : "#ffebee",
                  color: item.statuses[2] === "pass" ? "#2e7d32" : "#d32f2f",
                }}
              >
                {item.statuses[2].toUpperCase()}
              </Box>
            </Box>
          </Box>

          <Typography variant="body2">
            Count: <span style={{ fontWeight: 500 }}>{item.value}</span>
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Percentage: <span style={{ fontWeight: 500 }}>{percentage}%</span>
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Results Distribution
        </Typography>
      </Box>

      <Box sx={{ height: 500 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 70 }} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fontSize: 12 }}
              interval={0}
            />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
